import React, { useState } from "react";

const COL = {
  eau:       "#378ADD",
  gaz:       "#EF9F27",
  batterie:  "#1D9E75",
  eauxUsees: "#888780",
  header:    "#2D5A3D",
  dark:      "#1C3A28",
  text:      "#1C1C1E",
  muted:     "#8A8A8E",
  bg:        "#C4622D",
  border:    "#D3D1C7",
};

/* ── Calcul autonomie ───────────────────────────────────────── */
function calcJours(vals, caps, conds) {
  const { eau = 70, gaz = 8, batterie = 80, eauxUsees = 20 } = vals;
  const { cap_eau = 100, cap_gaz = 13, cap_eaux = 80 } = caps;
  const { personnes = 2, temp = 18, soleil = 5 } = conds;

  const eauJours = personnes > 0
    ? Math.max(0, Math.floor(eau / (12 * personnes)))
    : 99;

  const gazMult = temp < 0 ? 3 : temp < 10 ? 2 : temp < 18 ? 1.5 : 1;
  const gazJours = Math.max(0, Math.floor(gaz / (0.5 * gazMult)));

  const consoBat = soleil < 3 ? 15 : soleil <= 7 ? 10 : 5;
  const batJours = Math.max(0, Math.floor(batterie / consoBat));

  const eauxJours = cap_eaux > 0
    ? Math.max(0, Math.floor((cap_eaux - eauxUsees) / 8))
    : 99;

  return { eauJours, gazJours, batJours, eauxJours };
}

function alertLevel(vals, caps, jours) {
  const { eau = 70, gaz = 8, batterie = 80, eauxUsees = 20 } = vals;
  const { cap_eau = 100, cap_gaz = 13, cap_eaux = 80 } = caps;
  const min = Math.min(jours.eauJours, jours.gazJours, jours.batJours);
  if (min <= 1 || eau < cap_eau * 0.20 || gaz < cap_gaz * 0.15 || batterie < 20 || eauxUsees > cap_eaux * 0.85) return "rouge";
  if (min <= 3 || eau < cap_eau * 0.40 || gaz < cap_gaz * 0.30 || batterie < 40 || eauxUsees > cap_eaux * 0.70) return "orange";
  return "vert";
}

function getConseil(conds, jours, vals, caps) {
  const { personnes, temp, soleil } = conds;
  const { eauxUsees = 20 } = vals;
  const { cap_eaux = 80 } = caps;
  const min = Math.min(jours.eauJours, jours.gazJours, jours.batJours);
  if (eauxUsees > cap_eaux * 0.85) return "Les eaux usées sont presque pleines — videz-les en priorité.";
  if (temp < 0)       return `Grand froid (${temp}°C) : le gaz est consommé 3× plus vite. Prévoyez une recharge.`;
  if (temp < 10)      return `Froid (${temp}°C) : consommation de gaz doublée. Surveillez vos réserves.`;
  if (temp < 18)      return `Températures fraîches (${temp}°C) : gaz ×1.5. Gardez un œil sur la bouteille.`;
  if (soleil < 3 && jours.batJours < 5) return "Peu d'ensoleillement : limitez les consommations électriques.";
  if (personnes >= 4) return `${personnes} personnes à bord : l'eau tiendra ~${jours.eauJours} jour${jours.eauJours !== 1 ? "s" : ""}.`;
  if (soleil > 7)     return "Excellent ensoleillement : la batterie se recharge efficacement.";
  if (min >= 7)       return "Conditions idéales — partez l'esprit tranquille !";
  return `Autonomie estimée : ${min} jour${min !== 1 ? "s" : ""}. Rechargez les ressources critiques avant de partir.`;
}

function getCritique(jours) {
  return [
    { label: "Eau",      j: jours.eauJours },
    { label: "Gaz",      j: jours.gazJours },
    { label: "Batterie", j: jours.batJours },
  ].reduce((a, b) => (a.j <= b.j ? a : b));
}

const ALERT_THEME = {
  vert:   { bg: "#EAF3DE", color: "#085041", border: "#5DCAA5" },
  orange: { bg: "#FFF3E0", color: "#A85E00", border: "#EF9F27" },
  rouge:  { bg: "#FDECEA", color: "#B53120", border: "#C4622D" },
};

function fmtNum(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}

/* ── Section capacités ──────────────────────────────────────── */
function CapacityField({ label, value, unit, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: COL.text, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="number"
          min={1}
          value={value}
          placeholder="—"
          onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          style={{
            flex: 1, padding: "10px 12px",
            border: `1.5px solid ${value === "" ? "#C4622D80" : COL.border}`,
            borderRadius: 10,
            fontSize: 16, fontWeight: 700, color: COL.text,
            fontFamily: "inherit", background: "#F8F6F1",
            outline: "none",
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: COL.muted, minWidth: 24 }}>
          {unit}
        </span>
      </div>
    </div>
  );
}

function CapacitesSection({ rawCaps, caps, onSave, t = {} }) {
  const configured = rawCaps.cap_eau != null;
  const [open, setOpen] = useState(!configured);
  const [draft, setDraft] = useState({
    cap_eau:  configured ? rawCaps.cap_eau  : "",
    cap_gaz:  configured ? rawCaps.cap_gaz  : "",
    cap_eaux: configured ? rawCaps.cap_eaux : "",
  });

  const set = (key, val) => setDraft(p => ({ ...p, [key]: val }));

  const isValid =
    draft.cap_eau  !== "" && Number(draft.cap_eau)  > 0 &&
    draft.cap_gaz  !== "" && Number(draft.cap_gaz)  > 0 &&
    draft.cap_eaux !== "" && Number(draft.cap_eaux) > 0;

  function handleValider() {
    onSave({ cap_eau: Number(draft.cap_eau), cap_gaz: Number(draft.cap_gaz), cap_eaux: Number(draft.cap_eaux) });
    setOpen(false);
  }

  function handleModifier() {
    setDraft({ cap_eau: caps.cap_eau, cap_gaz: caps.cap_gaz, cap_eaux: caps.cap_eaux });
    setOpen(true);
  }

  return (
    <div style={{
      background: "white", borderRadius: 14,
      padding: "14px 16px", marginBottom: 12,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      border: `1.5px solid ${COL.border}`,
    }}>
      {/* En-tête */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: COL.muted, letterSpacing: 1 }}>
            {t.capacitesVan || "CAPACITÉS DU VAN"}
          </div>
          {configured && !open && (
            <div style={{ fontSize: 12, color: COL.text, marginTop: 4 }}>
              💧 {caps.cap_eau} L · 🔥 {fmtNum(caps.cap_gaz)} kg · 🪣 {caps.cap_eaux} L
            </div>
          )}
        </div>
        {configured && !open && (
          <button
            onClick={handleModifier}
            style={{
              background: "none", border: `1.5px solid ${COL.border}`,
              borderRadius: 8, padding: "5px 10px",
              fontSize: 12, fontWeight: 700, color: COL.muted,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {"✏️ " + (t.modifier || "Modifier")}
          </button>
        )}
      </div>

      {/* Formulaire */}
      {open && (
        <div style={{ marginTop: 16 }}>
          <CapacityField label={t.capaciteEau || "Capacité eau"} value={draft.cap_eau} unit="L"
            onChange={v => set("cap_eau", v)} />
          <CapacityField label={t.capaciteGaz || "Capacité gaz"} value={draft.cap_gaz} unit="kg"
            onChange={v => set("cap_gaz", v)} />
          <CapacityField label={t.capaciteEauxUsees || "Capacité eaux usées"} value={draft.cap_eaux} unit="L"
            onChange={v => set("cap_eaux", v)} />
          <button
            onClick={handleValider}
            disabled={!isValid}
            style={{
              width: "100%", padding: "12px 0",
              background: isValid ? COL.header : "#C8C8C8",
              color: "white", border: "none", borderRadius: 11,
              fontSize: 14, fontWeight: 800,
              cursor: isValid ? "pointer" : "not-allowed",
              fontFamily: "inherit", marginTop: 4,
              transition: "background 0.2s",
            }}
          >
            {t.valider || "Valider"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Jauge ──────────────────────────────────────────────────── */
function GaugeCard({ id, label, unit, value, max, step, color, joursLabel, isAlert, alertMsg, onChange, showCapacity = true, t = {} }) {
  const safeVal  = Math.min(value, max);
  const pct      = max > 0 ? Math.round((safeVal / max) * 100) : 0;
  const barColor = isAlert ? "#C4622D" : color;

  return (
    <div style={{
      background: "white", borderRadius: 14,
      padding: "15px 16px", marginBottom: 10,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      border: `1.5px solid ${isAlert ? "#C4622D50" : COL.border}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>{label}</div>
          <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>{joursLabel}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: 22, fontWeight: 900, color: isAlert ? "#C4622D" : color }}>
            {fmtNum(safeVal)}
          </span>
          <span style={{ fontSize: 12, color: COL.muted, marginLeft: 2 }}>{unit}</span>
          {showCapacity && (
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
              {t.surLabel || "sur"} {fmtNum(max)} {unit} · {pct}%
            </div>
          )}
        </div>
      </div>

      <div style={{ height: 8, background: "#EDEDEA", borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: barColor, borderRadius: 99,
          transition: "width 0.3s ease",
        }} />
      </div>

      <input
        type="range" min={0} max={max} step={step} value={safeVal}
        onChange={e => onChange(id, Number(e.target.value))}
        style={{ width: "100%", accentColor: barColor, cursor: "pointer" }}
      />

      {isAlert && alertMsg && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#C4622D", fontWeight: 600 }}>
          {alertMsg}
        </div>
      )}
    </div>
  );
}

/* ── Slider conditions ──────────────────────────────────────── */
function CondSlider({ label, value, min, max, step = 1, unit, color, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}{unit}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: color, cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COL.muted, marginTop: 2 }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

/* ── Écran principal ────────────────────────────────────────── */
export default function AutonomiScreen({ autonomie, setAutonomie, t = {} }) {
  const raw = autonomie || {};

  const configured = raw.cap_eau != null;

  const caps = {
    cap_eau:  raw.cap_eau  ?? 100,
    cap_gaz:  raw.cap_gaz  ?? 13,
    cap_eaux: raw.cap_eaux ?? 80,
  };

  const vals = {
    eau:       Math.min(raw.eau       != null ? raw.eau       : caps.cap_eau,  caps.cap_eau),
    gaz:       Math.min(raw.gaz       != null ? raw.gaz       : caps.cap_gaz,  caps.cap_gaz),
    batterie:  raw.batterie  != null ? raw.batterie : 80,
    eauxUsees: Math.min(raw.eauxUsees != null ? raw.eauxUsees : 0,             caps.cap_eaux),
  };

  const [conds, setConds] = useState({ personnes: 2, temp: 18, soleil: 5 });

  const jours   = calcJours(vals, caps, conds);
  const level   = alertLevel(vals, caps, jours);
  const conseil = getConseil(conds, jours, vals, caps);
  const critique = getCritique(jours);
  const theme   = ALERT_THEME[level];

  const setLevel = (id, val) =>
    setAutonomie(prev => ({ ...(prev || {}), [id]: val }));

  const saveCaps = (draft) =>
    setAutonomie(prev => ({ ...(prev || {}), ...draft }));

  const setCond = (key, val) =>
    setConds(prev => ({ ...prev, [key]: val }));

  return (
    <div style={{ background: COL.bg, minHeight: "100%", paddingBottom: 24 }}>

      {/* Header */}
      <div style={{ background: COL.header, padding: "20px 20px 18px" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#F8F6F1", textAlign: "center" }}>{t.autonomie || "Autonomie"} / Stats</div>
        <div style={{ fontSize: 12, color: "rgba(248,246,241,0.65)", marginTop: 3, textAlign: "center" }}>
          {t.predictionsAvantDepart || "Prédictions avant départ"}
        </div>
      </div>

      <div style={{ padding: "16px 16px 0" }}>

        {/* ── Capacités du van ── */}
        <CapacitesSection rawCaps={raw} caps={caps} onSave={saveCaps} t={t} />

        {/* ── Contenu conditionnel ── */}
        {!configured ? (
          <div style={{
            background: "white", borderRadius: 14,
            padding: "36px 20px", textAlign: "center",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            border: `1.5px solid ${COL.border}`,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🚐</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: COL.muted, lineHeight: 1.5 }}>
              {t.renseignezCapacites || "Renseignez les capacités de votre van pour commencer"}
            </div>
          </div>
        ) : (
          <>
            {/* ── Alerte globale ── */}
            <div style={{
              background: theme.bg, borderRadius: 14,
              padding: "14px 16px", marginBottom: 12,
              border: `1.5px solid ${theme.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: theme.color }}>
                  {level === "vert"   && (t.autonomieSuffisante   || "✓ Autonomie suffisante")}
                  {level === "orange" && (t.autonomieLimitee       || "⚠ Autonomie limitée")}
                  {level === "rouge"  && (t.ressourcesCritiques    || "⛽ Ressources critiques")}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.color }}>
                  {Math.min(jours.eauJours, jours.gazJours, jours.batJours)} j
                </div>
              </div>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.eau }}>
                  💧 {t.eau || "Eau"} — {jours.eauJours} {jours.eauJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.gaz }}>
                  🔥 {t.gaz || "Gaz"} — {jours.gazJours} {jours.gazJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.eauxUsees }}>
                  🚿 {t.eauxUsees || "Eaux usées"} — {jours.eauxJours} {jours.eauxJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")} {t.avantVidange || "avant vidange"}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.batterie }}>
                  🔋 {t.batterie || "Batterie"} — {jours.batJours} {jours.batJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}
                </div>
              </div>
            </div>

            {/* ── Conditions de voyage ── */}
            <div style={{
              background: "white", borderRadius: 14,
              padding: "16px", marginBottom: 12,
              boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 16 }}>
                {t.conditionsVoyage || "CONDITIONS DE VOYAGE"}
              </div>
              <CondSlider
                label={t.personnesABord || "Personnes à bord"} value={conds.personnes} min={1} max={6}
                unit=" pers." color={COL.eau}
                onChange={v => setCond("personnes", v)}
              />
            </div>

            {/* ── Jauges ── */}
            <GaugeCard
              id="eau" label={t.eau || "Eau"} unit="L"
              max={caps.cap_eau} step={1}
              value={vals.eau}
              color={COL.eau}
              joursLabel={`~${jours.eauJours} ${jours.eauJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}`}
              isAlert={vals.eau < caps.cap_eau * 0.20}
              alertMsg={t.reservoirCritique || "Réservoir critique — trouvez un point d'eau"}
              onChange={setLevel}
              t={t}
            />
            <GaugeCard
              id="gaz" label={t.gaz || "Gaz"} unit="kg"
              max={caps.cap_gaz} step={0.5}
              value={vals.gaz}
              color={COL.gaz}
              joursLabel={`~${jours.gazJours} ${jours.gazJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}`}
              isAlert={vals.gaz < caps.cap_gaz * 0.15}
              alertMsg={t.bouteilleVide || "Bouteille presque vide — rechargez bientôt"}
              onChange={setLevel}
              t={t}
            />
            <GaugeCard
              id="eauxUsees" label={t.eauxUsees || "Eaux usées"} unit="L"
              max={caps.cap_eaux} step={1}
              value={vals.eauxUsees}
              color={COL.eauxUsees}
              joursLabel={`~${jours.eauxJours} ${jours.eauxJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")} ${t.avantVidange || "avant vidange"}`}
              isAlert={vals.eauxUsees > caps.cap_eaux * 0.70}
              alertMsg={t.tankCharge || "Tank chargé — prévoyez une vidange"}
              onChange={setLevel}
              t={t}
            />
            <GaugeCard
              id="batterie" label={t.batterie || "Batterie"} unit="%"
              max={100} step={5}
              value={vals.batterie}
              color={COL.batterie}
              joursLabel={`~${jours.batJours} ${jours.batJours !== 1 ? (t.jours || "jours") : (t.jour || "jour")}`}
              isAlert={vals.batterie < 20}
              alertMsg={t.batterieFaible || "Batterie faible — branchez ou exposez les panneaux"}
              onChange={setLevel}
              showCapacity={false}
              t={t}
            />
          </>
        )}

      </div>
    </div>
  );
}
